import { motion } from "framer-motion";
import { IMAGES } from "@/data/content";

const CHAPTERS = [
  {
    num: "I",
    title: "The Philosophy",
    body: "The best copy does not call attention to how hard the writer worked. It helps the audience recognize a need, understand a product, and feel ready for the next step.",
  },
  {
    num: "II",
    title: "The Technique",
    body: "Audience insight, editorial craft, and conversion principles — held together by a practical understanding of how content moves through marketing, product, and operations teams.",
  },
  {
    num: "III",
    title: "The Range",
    body: "Two speculative brands, two registers. VoltHaus proves technical clarity for consumer electronics; MemoryVault proves warmth for family storytelling. Same discipline, different voice.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Manifesto = () => {
  return (
    <section id="about" className="py-24 sm:py-32 lg:py-40" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={0}
          className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-6"
        >
          A working manifesto
        </motion.p>
        <motion.h2
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={1}
          className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15] max-w-2xl"
        >
          Copywriting as both a <em className="text-wine">creative discipline</em> and a practical business tool.
        </motion.h2>

        <div className="mt-16 lg:mt-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 flex flex-col gap-0">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.num}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                custom={i + 1}
                className={`group grid gap-4 sm:gap-8 py-10 border-t border-line last:border-b ${c.num === "III" ? "sm:grid-cols-[120px_1fr_170px]" : "sm:grid-cols-[120px_1fr]"}`}
                data-testid={`manifesto-chapter-${c.num.toLowerCase()}`}
              >
                <div className="flex sm:flex-col items-baseline sm:items-start gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">Chapter</span>
                  <span className="font-serif text-5xl text-wine/85 transition-transform duration-500 group-hover:-translate-y-1">{c.num}</span>
                </div>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl leading-[1.25] mb-3">{c.title}</h3>
                  <p className="text-base leading-relaxed text-subtle max-w-xl">{c.body}</p>
                </div>
                {c.num === "III" && (
                  <div className="spotlight-frame vignette relative overflow-hidden border border-ink/10 self-start" data-testid="manifesto-portrait-frame">
                    <img
                      src={IMAGES.portrait}
                      alt="Melinda Adams, copywriter and content writer"
                      className="w-full h-56 object-cover object-[50%_18%] transition-transform duration-700 hover:scale-[1.03]"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={2}
              className="lg:sticky lg:top-28"
            >
              <div className="spotlight-frame vignette relative overflow-hidden border border-ink/10">
                <img
                  src={IMAGES.typewriter}
                  alt="Vintage typewriter on a writer's desk in warm light"
                  className="w-full h-[380px] object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </div>
              <div className="mt-6 bg-cream border border-line p-6">
                <p className="font-serif italic text-xl leading-snug">
                  "I intentionally selected two very different brands to display my voice range."
                </p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                  — From the portfolio preface
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
